/**
 * The importer's runs (piece 31; decisions 0921, 0922): the presets, the Zoho
 * Books pull's blocks in their order, what a run records per source row, and
 * the answers the import screen reads.
 */
import { normaliseCompanyName } from '../records/normalise';
import { fill, t, type StringKey } from '../strings';
import { addCalendarDays, isCalendarDay } from '../zonedTime';
import type { BooksKeyProbe, BooksReportSection } from './booksSource';
import type { SoftwareRevision } from './importMatchLog';
import type { ImportStagedDecisionValue } from './importStaging';
import type { UncategorizedReportSummary } from './uncategorizedReport';
import type { ImportPassesSummary } from './importSteps';
import type { ZohoBooksSyncView } from './zohoBooksSync';

/** 🔒 Decision 9280 adds `CsvFile`, the CSV file preset, one entity's file a run; decision 12660 adds `ManufacturerLookup`, staged and stepped from the Items list, never the Import page; decision 12720 adds `ZohoExpense`. */
export const IMPORT_PRESETS = ['ZohoBooks', 'ZohoCrm', 'ZohoExpense', 'CsvFile', 'ManufacturerLookup'] as const;
export type ImportPresetValue = (typeof IMPORT_PRESETS)[number];

/** The blocks in their order; block 4, the documents, runs over a date range (decision 0930), and block 5, the bank transactions, over one too (decision 8040). */
export const IMPORT_BLOCKS = ['AccountsAndTaxes', 'Contacts', 'Items', 'Documents', 'BankTransactions', 'Manufacturers', 'ExpenseReports', 'CsvCompanies', 'CsvPeople', 'CsvLocations', 'CsvItems', 'ItemLookup'] as const;
export type ImportBlockValue = (typeof IMPORT_BLOCKS)[number];

/** The blocks built, in the order the screen offers them. */
export const BUILT_IMPORT_BLOCKS: readonly ImportBlockValue[] = ['AccountsAndTaxes', 'Contacts', 'Items', 'Documents', 'BankTransactions'];

/** 🔒 The Zoho CRM preset's blocks (decision 8380): Manufacturers alone, which no Books block waits on. */
export const CRM_IMPORT_BLOCKS: readonly ImportBlockValue[] = ['Manufacturers'];

/** 🔒 The CSV file preset's blocks (decision 9280): one entity's file a run, none waiting on another. */
export const CSV_IMPORT_BLOCKS: readonly ImportBlockValue[] = ['CsvCompanies', 'CsvPeople', 'CsvLocations', 'CsvItems'];

/** 🔒 The manufacturer lookup's one block (decision 12660), which the Import page never offers. */
export const LOOKUP_IMPORT_BLOCKS: readonly ImportBlockValue[] = ['ItemLookup'];

/** 🔒 Decision 12720: the Zoho Expense preset's one block, the expense reports from the cut-off. */
export const EXPENSE_IMPORT_BLOCKS: readonly ImportBlockValue[] = ['ExpenseReports'];

/** 🔒 The preset a block belongs to (decisions 8380, 9280, 12660, 12720). */
export function presetOfBlock(block: ImportBlockValue): ImportPresetValue {
  if (LOOKUP_IMPORT_BLOCKS.includes(block)) return 'ManufacturerLookup';
  if (CSV_IMPORT_BLOCKS.includes(block)) return 'CsvFile';
  if (EXPENSE_IMPORT_BLOCKS.includes(block)) return 'ZohoExpense';
  return CRM_IMPORT_BLOCKS.includes(block) ? 'ZohoCrm' : 'ZohoBooks';
}

/** 🔒 The blocks a person's read stages as contacts (decisions 7980, 8380): Contacts, and the CRM's Manufacturers, which reads each manufacturer as one. */
export const CONTACT_BLOCKS: readonly ImportBlockValue[] = ['Contacts', 'Manufacturers'];

/**
 * 🔒 A block run before its dependencies is refused (piece 31). Block 3 needs a
 * finished pull of block 1, because an item's accounts are found through block
 * 1's source references; block 2 needs no other block. Block 4 needs blocks 2
 * and 3, because a document's contact, items and accounts are found through
 * their references (decision 0930). Block 5 needs block 4, because a line is matched to
 * the payment the documents wrote (decision 8040).
 */
export const IMPORT_BLOCK_DEPENDS_ON: Readonly<Record<ImportBlockValue, readonly ImportBlockValue[]>> = {
  AccountsAndTaxes: [],
  Contacts: [],
  Items: ['AccountsAndTaxes'],
  Documents: ['Contacts', 'Items'],
  BankTransactions: ['Documents'],
  Manufacturers: [],
  // 🔒 Decision 12720: the expense reports link to the journals a documents range wrote, so they wait on one.
  ExpenseReports: ['Documents'],
  CsvCompanies: [],
  CsvPeople: [],
  CsvLocations: [],
  CsvItems: [],
  ItemLookup: [],
};

/** The blocks that run over a date range: block 4 and block 5 (decision 8040). */
export function blockTakesRange(block: ImportBlockValue): boolean {
  return block === 'Documents' || block === 'BankTransactions';
}

/**
 * A run's mode (decisions 1055, 1483). Every run since decision 1483 is
 * Stepped: it reads its scope once into staged rows, and a person writes them
 * all or steps through them. Whole marks a run from before it, which read and
 * wrote in one pass and kept only its `rows`. Sync marks a daily pull's run
 * (decision 3801): read and written like a Stepped run, by the job clock.
 */
export const IMPORT_RUN_MODES = ['Whole', 'Stepped', 'Sync'] as const;
export type ImportRunModeValue = (typeof IMPORT_RUN_MODES)[number];

/** The one source kind a run's scope names: a block's top-level source records (decision 1055). */
export const IMPORT_SCOPE_KINDS = ['Account', 'Tax', 'Contact', 'Item', 'Estimate', 'SalesOrder', 'Invoice', 'CreditNote', 'CustomerPayment', 'PurchaseOrder', 'Bill', 'VendorCredit', 'VendorPayment', 'Expense', 'ManualJournal', 'InventoryAdjustment', 'BankTransaction', 'CsvRow', 'ExpenseReport'] as const;
export type ImportScopeKindValue = (typeof IMPORT_SCOPE_KINDS)[number];

/** 🔒 The kinds each block's scope offers (decision 1055): block 1's accounts and taxes, block 2's contacts, block 3's items, and block 4's eleven document kinds in the order of a sale (decision 3340) with the inventory adjustments read last (decision 3660). */
export const IMPORT_SCOPE_KINDS_OF: Readonly<Record<ImportBlockValue, readonly ImportScopeKindValue[]>> = {
  AccountsAndTaxes: ['Account', 'Tax'],
  Contacts: ['Contact'],
  Items: ['Item'],
  Documents: ['Estimate', 'SalesOrder', 'Invoice', 'CreditNote', 'CustomerPayment', 'PurchaseOrder', 'Bill', 'VendorCredit', 'VendorPayment', 'Expense', 'ManualJournal', 'InventoryAdjustment'],
  BankTransactions: ['BankTransaction'],
  // 🔒 Decision 8380: a Manufacturers read reads the whole module, or one manufacturer by its CRM id, as a Revisit reads it.
  Manufacturers: ['Contact'],
  // 🔒 Decision 12720: an Expense Reports read reads every report from the cut-off, or one report by its Zoho id, as a Revisit reads it.
  ExpenseReports: ['ExpenseReport'],
  // 🔒 Decision 9280: a CSV run reads its whole file, and its rows are CsvRow.
  CsvCompanies: ['CsvRow'],
  CsvPeople: ['CsvRow'],
  CsvLocations: ['CsvRow'],
  CsvItems: ['CsvRow'],
  // 🔒 Decision 12660: a lookup run names its Items row by row and offers no scope; its rows are LookupItem.
  ItemLookup: [],
};

/** A run's scope: within its block, one source kind, one record by its source id, and a date range; every part empty reads the whole block. */
export interface ImportRunScope {
  scopeKind: ImportScopeKindValue | null;
  scopeSourceId: string | null;
  rangeFrom: string | null;
  rangeTo: string | null;
}

/**
 * 🔒 Whether a scope may run on a block (decision 1055), as the refusal a
 * person reads, or null. A kind is one the block offers, and a one-record scope
 * names its kind. A range on blocks 1 to 3 is two calendar days, the earlier
 * first and neither after today; block 4's range keeps its own rules.
 */
export function runScopeProblem(block: ImportBlockValue, scope: ImportRunScope, today: string): string | null {
  if (scope.scopeKind !== null && !IMPORT_SCOPE_KINDS_OF[block].includes(scope.scopeKind)) return 'That kind of record is not read by this block; choose one the block offers.';
  if (scope.scopeSourceId !== null && scope.scopeKind === null) return 'A run of one record names its kind as well as its Zoho id; choose the kind first.';
  if (blockTakesRange(block)) return null;
  if (scope.rangeFrom === null && scope.rangeTo === null) return null;
  if (!isCalendarDay(scope.rangeFrom) || !isCalendarDay(scope.rangeTo)) return 'A range names two calendar days, the first and the last it reads.';
  if (scope.rangeFrom > scope.rangeTo) return 'The range starts after it ends; put the earlier day first.';
  if (scope.rangeTo > today) return 'The range ends after today; end it today or earlier.';
  return null;
}

/** A scope's parts as the scope a run records: a blank source id reads as none. */
export function normalisedScope(input: { scopeKind?: unknown; scopeSourceId?: unknown; rangeFrom?: unknown; rangeTo?: unknown }): ImportRunScope | null {
  const kind = input.scopeKind === undefined || input.scopeKind === null || input.scopeKind === '' ? null : input.scopeKind;
  if (kind !== null && !(IMPORT_SCOPE_KINDS as readonly unknown[]).includes(kind)) return null;
  const text = (value: unknown) => (typeof value === 'string' && value.trim() !== '' ? value.trim() : null);
  return { scopeKind: kind as ImportScopeKindValue | null, scopeSourceId: text(input.scopeSourceId), rangeFrom: text(input.rangeFrom), rangeTo: text(input.rangeTo) };
}

/**
 * 🔒 The last day a documents range counts as written through (decisions 1055,
 * 1483): a day counts once each of the twelve kinds has been written over it by
 * a finished run, whole, scoped or stepped (decisions 3340, 3660). A run of one kind
 * covers that kind, a run naming no kind covers every kind, and a run of one
 * record covers none.
 */
export function documentsPulledThrough(runs: readonly { scopeKind: ImportScopeKindValue | null; scopeSourceId: string | null; rangeTo: string }[]): string | null {
  const through = new Map<ImportScopeKindValue, string>();
  for (const run of runs) {
    if (run.scopeSourceId !== null) continue;
    for (const kind of run.scopeKind === null ? IMPORT_SCOPE_KINDS_OF.Documents : [run.scopeKind]) {
      const was = through.get(kind);
      if (was === undefined || run.rangeTo > was) through.set(kind, run.rangeTo);
    }
  }
  let least: string | null = null;
  for (const kind of IMPORT_SCOPE_KINDS_OF.Documents) {
    const day = through.get(kind);
    if (day === undefined) return null;
    if (least === null || day < least) least = day;
  }
  return least;
}

/** A run is Staged once every page is read, and Finished once Write all or Close run ends it (decisions 1055, 1483). */
export const IMPORT_RUN_STATUSES = ['Running', 'Staged', 'Finished', 'Cancelled', 'Failed'] as const;
export type ImportRunStatusValue = (typeof IMPORT_RUN_STATUSES)[number];

export const IMPORT_OUTCOMES = ['Created', 'Updated', 'Unchanged', 'Rejected'] as const;
export type ImportOutcome = (typeof IMPORT_OUTCOMES)[number];

/**
 * The kinds of source record a `SourceRef` names; block 4 adds its eleven document kinds (decisions 0930, 3340), its
 * opening balances and the invoices and bills open at the cut-off, keyed on the cut-off day (decision 2991), and each
 * attachment a document lists, keyed on Zoho's attachment id (decision 3480), and each inventory adjustment's line, keyed on
 * the adjustment's id and the line's (decision 3660). Block 3 adds each item's picture, keyed on the item's id and the
 * image's (decision 3661).
 */
export const SOURCE_KINDS = ['Account', 'Tax', 'Contact', 'ContactAddress', 'ContactPerson', 'Item', 'Invoice', 'CreditNote', 'CustomerPayment', 'Bill', 'VendorCredit', 'VendorPayment', 'Expense', 'ManualJournal', 'OpeningBalance', 'OpenDocuments', 'Estimate', 'SalesOrder', 'PurchaseOrder', 'Attachment', 'InventoryAdjustment', 'ItemImage', 'BankTransaction', 'ExpenseReport', 'Receipt'] as const;
export type SourceKindValue = (typeof SOURCE_KINDS)[number];

export const SOURCE_RECORD_TYPES = ['Account', 'SalesTaxRate', 'LocalTaxJurisdiction', 'CompanyRecord', 'SiteRecord', 'PersonRecord', 'Item', 'Invoice', 'CreditNote', 'Payment', 'Bill', 'VendorCredit', 'JournalEntry', 'Quote', 'SalesOrder', 'PurchaseOrder', 'File', 'StockMovement', 'ExpenseReport', 'ExpenseLine'] as const;
export type SourceRecordTypeValue = (typeof SOURCE_RECORD_TYPES)[number];

/** How a contact met a company here, for block 2's three counts (piece 31). */
/** 🔒 Decision 6332 adds `emailDomain`, a match on the domain of a contact's website or its people's emails. */
/** 🔒 Decision 8381 adds `itemMaker`, a CRM manufacturer matched on the maker an item here names. */
export type ContactMatch = 'reference' | 'ein' | 'emailDomain' | 'namePostal' | 'itemMaker' | 'none';

/** 🔒 A placeholder site a document's write opened for its customer (decision 4802): the company and its name, which the cleanup list opens to add the real address. */
export interface PlaceholderSiteOpened {
  companyId: string;
  companyName: string;
  siteName: string;
}

/**
 * One entry of a run's `rows` column: one source row read. 🔒 A rejected entry
 * carries its reason in the same entry, so there is no second table.
 */
export interface ImportRowEntry {
  sourceKind: SourceKindValue;
  sourceId: string;
  label: string;
  outcome: ImportOutcome;
  /** The record here the row reached or would reach, as a person reads it. */
  target: string | null;
  /** The record's own route, where the row reached one. */
  route: string | null;
  reason: string | null;
  notes: string[];
  /** Block 2: how the contact met a company here. */
  match?: ContactMatch;
  /** Block 3: the unit text a rejection could not place, for the unit match. */
  unitText?: string;
  /** Block 2: the custom-field definitions a populated field with no home writes or would write. */
  customFields?: string[];
  /** Block 3: the manufacturer the row's write creates as a company, or created (decision 1514). */
  createsManufacturer?: string;
  /** Block 2: what the address verifier made of a building the write opened (decision 1923); absent on a read and on a building already here. */
  addressCheck?: ImportAddressCheck;
  /** 🔒 The opening row: each balance-sheet account no pull linked, which a person places with Match or Create (decision 2030). */
  unlinked?: OpeningUnlinkedAccount[];
  /** 🔒 The opening row's staged decision, added to its entry when the run is answered and stored nowhere, so the rejected list draws no placing block for a decided row (T1980). */
  decision?: ImportStagedDecisionValue;
  /** 🔒 The placeholder sites this row's write opened, each with its company (decision 4802), which the cleanup list names; absent where the write opened none. */
  placeholderSites?: PlaceholderSiteOpened[];
  /** 🔒 A document refused for its opening row, or for the open documents' row after it: that row, which the rejected list opens from the reason (decisions 2031, 2991). */
  waitsOn?: { sourceKind: 'OpeningBalance' | 'OpenDocuments'; sourceId: string; sequence: number };
  /** 🔒 A document rejected for a record another block did not write: that block's row for it, which the rejected list and the cleanup list open (decision 3262). */
  waitsOnRow?: ImportWaitsOnRow;
  /** 🔒 An imported invoice held as a Draft because our tax differs from Zoho Books' (decision 11661): both amounts, the invoice, and the act a person chose. */
  taxHold?: ImportTaxHold;
}

/** 🔒 The two acts on a held invoice (decision 11661): Keep the Calculation issues it as our rates worked it out, Use Zoho's Tax with Zoho Books' amount. */
export const IMPORT_TAX_HOLD_ACTS = ['Calculation', 'Zoho'] as const;
export type ImportTaxHoldAct = (typeof IMPORT_TAX_HOLD_ACTS)[number];

/**
 * 🔒 The flag on an imported invoice's entry whose tax worked out here differs from Zoho Books' tax amount (decision
 * 11661): the invoice is held as a Draft, and the flag stands until a person chooses one of the two acts, which `decided`
 * then names. A row whose two amounts agree carries no flag.
 */
export interface ImportTaxHold {
  invoiceId: string;
  /** The tax our own rates worked out, in cents. */
  computedCents: number;
  /** Zoho Books' tax amount on the invoice, in cents. */
  zohoCents: number;
  decided?: ImportTaxHoldAct;
}

/**
 * 🔒 The row an earlier block holds for a record a document names (decision
 * 3262): the block, its run, and the row by its kind, Zoho id and place.
 */
export interface ImportWaitsOnRow {
  block: ImportBlockValue;
  runId: string;
  /** An earlier block's contact, item or account, or block 4's own estimate, sales order or purchase order a document came from (decision 3341). */
  sourceKind: 'Account' | 'Contact' | 'Item' | 'Estimate' | 'SalesOrder' | 'PurchaseOrder';
  sourceId: string;
  sequence: number;
}

/** 🔒 The number a person reads for each block (decision 3262): block 1 the chart and taxes, 2 the contacts, 3 the items, 4 the documents. */
export const IMPORT_BLOCK_NUMBER: Readonly<Record<ImportBlockValue, number>> = { AccountsAndTaxes: 1, Contacts: 2, Items: 3, Documents: 4, BankTransactions: 5, Manufacturers: 1, ExpenseReports: 1, CsvCompanies: 1, CsvPeople: 1, CsvLocations: 1, CsvItems: 1, ItemLookup: 1 };

/**
 * 🔒 One account an opening row's balance sheet names and no pull linked
 * (decision 2030): its Zoho id, name, section and balance, and the account here
 * a person matched it to or created for it, as a person reads it.
 */
export interface OpeningUnlinkedAccount {
  sourceAccountId: string;
  name: string;
  section: BooksReportSection;
  amountCents: number;
  placed: string | null;
}

/**
 * 🔒 The address verifier's verdict on a building the contacts write opened
 * (decision 1923): Verified where the provider answered the address as typed,
 * NeedsReview where it offered another or found none, NotAsked where no
 * provider answered or the provider failed.
 */
export type ImportAddressCheck = 'Verified' | 'NeedsReview' | 'NotAsked';

/** The note a written address carries where the verifier offered a different address or none (decision 1923). */
export const ADDRESS_NEEDS_REVIEW_NOTE = 'the address verifier did not confirm this address as typed; open its location and press Verify address';

/** A contacts write's verified, needs-review and not-asked buildings, counted for the run's summary (decision 1923). */
export function addressCheckCounts(rows: readonly ImportRowEntry[]): { verified: number; needsReview: number; notAsked: number } {
  const checked = rows.filter((row) => row.sourceKind === 'ContactAddress' && row.addressCheck);
  return {
    verified: checked.filter((row) => row.addressCheck === 'Verified').length,
    needsReview: checked.filter((row) => row.addressCheck === 'NeedsReview').length,
    notAsked: checked.filter((row) => row.addressCheck === 'NotAsked').length,
  };
}

/**
 * 🔒 The words a contacts read's possible-duplicate note opens with (decisions
 * 1625, 1880): *possible duplicate of Penn Mag Inc at the same address (in
 * this pull)*. The read writes the note and both lists draw their pill off it,
 * through `isPossibleDuplicateNote`, so the row carries no field of its own for it.
 */
export const POSSIBLE_DUPLICATE_NOTE = 'possible duplicate of';

/** The note a contact row carries for one record of another folded name at the same street address (decision 1880). */
export function possibleDuplicateNote(name: string, here: boolean): string {
  return `${POSSIBLE_DUPLICATE_NOTE} ${name} at the same address (${here ? 'already here' : 'in this pull'})`;
}

/** The words a contact row's possible same-person note carries (decision 2502). */
export const SAME_PERSON_NOTE = 'possibly the same person as';

/** 🔒 The note a contact row carries for one of its people whose name another contact's person shares (decision 2502). */
/** 🔒 A twin on a contact with no company name is on their own contact, never at themselves (decision 3083). */
export function samePersonNote(name: string, twinName: string, twinCompany: string | null): string {
  return `${name}, ${SAME_PERSON_NOTE} ${twinName} ${twinCompany ? `at ${twinCompany}` : 'on their own contact'} (in this pull)`;
}

export function isSamePersonNote(note: string): boolean {
  return note.includes(`, ${SAME_PERSON_NOTE} `);
}

/** 🔒 The note a contact row carries where its folded name joins a company at another postal code (decision 1880). */
export function secondAddressNote(name: string, planned = false): string {
  return `joins ${name}${planned ? ', which this run creates,' : ','} as a second address`;
}

/** 🔒 The words a contact's note opens with where its folded name is already held by two companies here (decision 1880). */
export const ALREADY_HERE_TWICE_NOTE = 'already here twice';

/** The note itself, naming the other company: the Duplicates page's Merge is the remedy, never the import. */
export function alreadyHereTwiceNote(other: string): string {
  return `${ALREADY_HERE_TWICE_NOTE}, beside ${other}: merge from the Duplicates page`;
}

/** Whether a note is a contacts read's possible-duplicate note. */
export function isPossibleDuplicateNote(note: string): boolean {
  return note.startsWith(`${POSSIBLE_DUPLICATE_NOTE} `);
}

/** 🔒 The reason the most rows Write all left share (decision 3260), and how many share it. */
export interface ImportLeftReason {
  reason: string;
  rows: number;
}

/**
 * 🔒 The reason the most of a run's left rows share (decision 3260), from each
 * reason's row count: a tie goes to the reason met first in the map's order,
 * which the caller fixes (the staging service lists them by count, then
 * category, then the reason's own text, so two reasons of equal count answer
 * the same sentence on every poll); null where no row is left.
 */
export function mostSharedReason(counts: ReadonlyMap<string, number>): ImportLeftReason | null {
  let best: ImportLeftReason | null = null;
  for (const [reason, rows] of counts) if (rows > 0 && (!best || rows > best.rows)) best = { reason, rows };
  return best;
}

/**
 * 🔒 Whether a block 4 range may run (decisions 0930, 0932), as the refusal a
 * person reads, or null. Both ends are calendar days, the start is not after
 * the end and the end is not after today. Once the ledger is open, a range
 * starting before the go-live date is refused, and so is one starting later
 * than the day after the latest range pulled.
 */
export function documentRangeProblem(input: { from: unknown; to: unknown; today: string; goLive: string | null; pulledThrough: string | null }): string | null {
  if (!isCalendarDay(input.from) || !isCalendarDay(input.to)) return 'a documents run names its range as two calendar days, the first and the last it reads';
  if (input.from > input.to) return 'the range starts after it ends; put the earlier day first';
  if (input.to > input.today) return 'the range ends after today; end it today or earlier';
  if (input.goLive && input.from < input.goLive) {
    return `the ledger opens on ${input.goLive}, where its opening entry stands, so a range cannot start before it`;
  }
  if (input.goLive && input.pulledThrough && input.from > addCalendarDays(input.pulledThrough, 1)) {
    return `documents are pulled through ${input.pulledThrough}, so the next range starts on ${addCalendarDays(input.pulledThrough, 1)} or earlier, leaving no gap in the ledger`;
  }
  return null;
}

/**
 * 🔒 A Bank Transactions range refused by name (decision 8040), or null: two calendar days, the earlier first and neither
 * after today, inside the ledger, starting on or after the go-live date and ending by the day the documents are pulled
 * through, since a line is matched to the payment the documents wrote.
 */
export function bankRangeProblem(input: { from: unknown; to: unknown; today: string; goLive: string | null; pulledThrough: string | null }): string | null {
  if (!isCalendarDay(input.from) || !isCalendarDay(input.to)) return 'a bank transactions run names its range as two calendar days, the first and the last it reads';
  if (input.from > input.to) return 'the range starts after it ends; put the earlier day first';
  if (input.to > input.today) return 'the range ends after today; end it today or earlier';
  if (!input.goLive || !input.pulledThrough) return 'the documents are not written through any day yet, and a bank line is matched to the payment they wrote; read and write a documents range first';
  if (input.from < input.goLive) return `the ledger opens on ${input.goLive}, where its opening entry stands, so a range cannot start before it`;
  if (input.to > input.pulledThrough) return `the documents are written through ${input.pulledThrough}, so the range ends on that day or earlier`;
  return null;
}

/**
 * 🔒 The two steps of a running read (decision 3164): Listing while a page is
 * asked for, Reading while its rows are handled.
 */
export const IMPORT_READ_STEPS = ['Listing', 'Reading'] as const;
export type ImportReadStep = (typeof IMPORT_READ_STEPS)[number];

/**
 * 🔒 A running read's progress row by row (T1233): the page it is on, the rows
 * of that page handled so far out of the page's count, and the seconds a
 * rate-limit wait has left, or null while none runs. 🔒 Decision 3164 adds the
 * step and the running total of rows handled across pages.
 */
export interface ImportReadProgress {
  step: ImportReadStep;
  page: number;
  read: number;
  of: number;
  /** The rows handled so far across every page, this one's included. */
  total: number;
  waitSeconds: number | null;
}

/** 🔒 The blocks that read each row whole after listing its page (decisions 1676, 3164): their running line is two steps. */
export const IMPORT_BLOCK_READS_WHOLE: Readonly<Record<ImportBlockValue, boolean>> = { AccountsAndTaxes: false, Contacts: true, Items: true, Documents: false, BankTransactions: false, Manufacturers: true, ExpenseReports: true, CsvCompanies: false, CsvPeople: false, CsvLocations: false, CsvItems: false, ItemLookup: false };

/** Each block's own words on the running line (decision 3164): the plural and the one. */
const READ_NOUNS: Readonly<Record<ImportBlockValue, { many: StringKey; one: StringKey }>> = {
  AccountsAndTaxes: { many: 'import.progress.noun.accounts', one: 'import.progress.noun.account' },
  Contacts: { many: 'import.progress.noun.contacts', one: 'import.progress.noun.contact' },
  Items: { many: 'import.progress.noun.items', one: 'import.progress.noun.item' },
  Documents: { many: 'import.progress.noun.documents', one: 'import.progress.noun.document' },
  BankTransactions: { many: 'import.progress.noun.bankLines', one: 'import.progress.noun.bankLine' },
  Manufacturers: { many: 'import.progress.noun.manufacturers', one: 'import.progress.noun.manufacturer' },
  ExpenseReports: { many: 'import.progress.noun.expenseReports', one: 'import.progress.noun.expenseReport' },
  CsvCompanies: { many: 'import.progress.noun.csvRows', one: 'import.progress.noun.csvRow' },
  CsvPeople: { many: 'import.progress.noun.csvRows', one: 'import.progress.noun.csvRow' },
  CsvLocations: { many: 'import.progress.noun.csvRows', one: 'import.progress.noun.csvRow' },
  CsvItems: { many: 'import.progress.noun.csvRows', one: 'import.progress.noun.csvRow' },
  ItemLookup: { many: 'import.progress.noun.items', one: 'import.progress.noun.item' },
};

/** The running read as the screen draws it (decision 3164): the step's line, the page's bar, the running count and the wait. */
export interface ImportReadLine {
  line: string;
  /** The bar over the page's rows, while a page's rows are read; null while a page is asked for. */
  bar: { value: number; max: number } | null;
  soFar: string | null;
  wait: string | null;
}

/**
 * 🔒 The running read's line (decision 3164; T2245): *Step 1 · Listing the items,
 * page 2* and *Step 2 · Reading each item, 105 of 200 on page 2* for a block
 * that reads each row whole, and one step, *Reading the documents, page 3 · 40
 * of 200*, for a block that reads nothing whole. The bar measures the page's
 * rows; no bar measures the whole read, because the source gives no total.
 */
export function importReadLine(block: ImportBlockValue, progress: ImportReadProgress): ImportReadLine {
  const nouns = { many: t(READ_NOUNS[block].many), one: t(READ_NOUNS[block].one) };
  const reading = progress.step === 'Reading' && progress.of > 0;
  const values = { many: nouns.many, one: nouns.one, page: progress.page, read: progress.read, of: progress.of, total: progress.total };
  const line = IMPORT_BLOCK_READS_WHOLE[block]
    ? fill(t(reading ? 'import.progress.step2' : 'import.progress.step1'), values)
    : reading ? fill(t('import.progress.oneStepRows'), values) : fill(t('import.progress.oneStep'), values);
  return {
    line,
    bar: reading ? { value: progress.read, max: progress.of } : null,
    soFar: progress.total > 0 ? fill(t('import.progress.soFar'), values) : null,
    wait: progress.waitSeconds === null ? null : fill(t('import.progress.wait'), { seconds: progress.waitSeconds }),
  };
}

/** A run as the screen reads it. */
export interface ImportRunView {
  id: string;
  preset: ImportPresetValue;
  block: ImportBlockValue;
  mode: ImportRunModeValue;
  /** The one source kind the run read, and the one record by its Zoho id; both empty for a whole block (decision 1055). */
  scopeKind: ImportScopeKindValue | null;
  scopeSourceId: string | null;
  /** The run's range, both ends calendar days: block 4 reads a document's own date, blocks 1 to 3 the source's last-modified time. */
  rangeFrom: string | null;
  rangeTo: string | null;
  overwriteLocalChanges: boolean;
  status: ImportRunStatusValue;
  runBy: string;
  startedAt: string;
  finishedAt: string | null;
  pagesRead: number;
  cancelRequested: boolean;
  createdCount: number;
  updatedCount: number;
  unchangedCount: number;
  rejectedCount: number;
  diffFileId: string | null;
  failureReason: string | null;
  /** 🔒 Write all is writing the run's staged rows now (decision 1483); the run stays Staged until it ends. */
  writing: boolean;
  /** The rows of this read written so far, by Write all or one at a time. */
  written: number;
  /** 🔨 The undecided Weak matches Write all leaves for Step through (decision 1483); a Staged run holding any stays Staged after Write all. */
  weakLeft: number;
  /** 🔒 The undecided contact rows Write all leaves because a one-name question on them waits (decision 2500); absent on a fixture written before it. */
  askLeft?: number;
  /**
   * 🔒 Decision 3260: the undecided rows of a Staged run whose Accept is held,
   * which Write all passes over and leaves for Step through (a unit to match, a
   * record changed here, a tax to place); absent on a fixture written before it.
   */
  heldLeft?: number;
  /** 🔒 Decision 3260: the rows of a Staged run whose decision a service or a row they wait on refused, left for Step through; absent on a fixture written before it. */
  refusedLeft?: number;
  /** 🔒 Decision 3260: the reason the most held or refused rows share, and how many share it; null where none is left. */
  leftReason?: ImportLeftReason | null;
  /** 🔒 Decision 5382: the contacts rows the study left NotChecked at the month's cap; absent on a fixture written before it. */
  notCheckedCap?: number;
  /** 🔒 A running read's progress row by row (T1233); null once the run is not reading, absent on a fixture written before it. */
  readProgress?: ImportReadProgress | null;
  /** 🔒 Decision 7980: a waiting three-pass Contacts read's pass and what each pass has left; null or absent on every other run. */
  passes?: ImportPassesSummary | null;
  /** 🔒 T3169: the software revision answering this read, and whether the run started before it was deployed (so under an earlier one); absent on a fixture written before it. */
  revision?: SoftwareRevision;
  startedBeforeRevision?: boolean;
  /**
   * 🔒 One entry per source row read (decision 1483): as the read proposed it
   * while the row is unwritten, as written once it is, and rejected with the
   * reason nothing was written once the run closes without it.
   */
  rows: ImportRowEntry[];
  /** 🔒 An items read's evidence (decision 1675): one item's key names from each service, or the refusal; empty on every other block. */
  sourceKeys?: BooksKeyProbe[] | null;
}

/** The last run of a block, without its rows or its evidence. */
export type ImportRunSummary = Omit<ImportRunView, 'rows' | 'sourceKeys'>;

/** Where the source answers from: the adapter over the held rows, the fake on a developer's machine, or nothing. */
export type BooksSourceKind = 'zoho' | 'sample' | 'none';

/** 🔒 The Zoho CRM preset as the Import page reads it (decision 8380). */
export interface ZohoCrmPresetView {
  source: BooksSourceKind;
  /** Whether the CRM's own refresh token is held. */
  connected: boolean;
  /** Whether the Books client's id, secret and data center are held, which Connect with Zoho CRM needs. */
  clientReady: boolean;
  /** The manufacturers module's API name, from its Settings row, or the sample CRM's; null until one is kept. */
  module: string | null;
  /** The redirect address to register in Zoho's console beside the Books one. */
  redirectUri: string;
  block: { block: ImportBlockValue; lastRun: ImportRunSummary | null; cleanup: { runId: string; entries: number } | null; writtenWholeAt: string | null };
  running: ImportRunSummary | null;
  staged: ImportRunSummary[];
}

export interface ZohoBooksPresetView {
  source: BooksSourceKind;
  /** Which of the five rows are held, by key; a sealed row's value is never sent. */
  rowsHeld: Record<string, boolean>;
  /** Each block's last run and whether its dependency is met. */
  blocks: {
    block: ImportBlockValue; lastRun: ImportRunSummary | null; dependencyMet: boolean;
    /** The last closed run of the block and how many entries its cleanup list holds, counted on the block's row (decision 1055). */
    cleanup: { runId: string; entries: number } | null;
    /** 🔒 When the block was last written whole, a finished run with no kind, record or range chosen (decision 1760); null on Documents, which always names a range. */
    writtenWholeAt: string | null;
  }[];
  /** The run reading its scope, or the run Write all is writing; one at a time (decision 1483). */
  running: ImportRunSummary | null;
  /** The read runs waiting to be written, each holding its block until Write all or Close run ends it (decisions 1055, 1483). */
  staged: ImportRunSummary[];
  /** Block 4's ledger dates (decision 0932): the go-live date, the last day a finished run wrote through, and the latest range a finished run covered. */
  documents: {
    goLive: string | null; pulledThrough: string | null; lastPulledRange: { from: string; to: string } | null;
    /** 🔒 The range last written, as a person marked its comparison checked, with who and when (decision 1760); null until marked, and again once a later range is written. */
    compared: { from: string; to: string; by: string; at: string } | null;
    /** 🔒 The opening Reverse the Opening Entry takes back (decision 3482); null where no go-live date stands, and an answer without it reads as none. */
    opening?: ImportOpeningView | null;
  };
  /**
   * 🔒 The Zoho Books web application's address for the organization (decision 1971), which a rejected row's link
   * is formed from; null on the sample books and wherever the rows do not name it. An answer without it links nothing.
   */
  booksApp?: string | null;
  /**
   * 🔒 The latest Uncategorized report (decision 2000): the `File` the latest Finished Items run wrote, its run, its time
   * and its acknowledgement; null until one is written. An answer without it reads as none.
   */
  uncategorized?: UncategorizedReportSummary | null;
  /** 🔒 Sync mode's switch and its end date (decision 3800), read on the Connect step; an answer without it reads off. */
  sync?: ZohoBooksSyncView | null;
}

/**
 * 🔒 The standing opening of block 4 (decision 3482): its cut-off and the
 * go-live date after it, the opening entry's number, null where the ledger
 * opened with nothing to post, and how many open documents it loaded.
 */
export interface ImportOpeningView {
  cutoff: string;
  goLive: string;
  entryNumber: string | null;
  documents: number;
}

/** What Reverse the Opening Entry answers (decision 3482): the cut-off it took back, its reversal's entry number and the open documents voided with it. */
export interface ImportOpeningReversed {
  cutoff: string;
  reversal: string | null;
  documents: number;
}

export interface ZohoBooksConnectionAnswer {
  ok: boolean;
  organisationName: string | null;
  message: string | null;
}

/** The kinds a run's entries name as one source row's first entry; a contact's addresses and people follow their contact. */
const CHILD_ENTRY_KINDS: readonly SourceKindValue[] = ['ContactAddress', 'ContactPerson'];

/**
 * 🔒 A run's entries with one source row's entries replaced (decision 1483):
 * the row's own entry and the children that follow it give way to `entries`,
 * in the same place. A row the list does not hold yet is added at its end.
 */
export function replaceRowEntries(rows: readonly ImportRowEntry[], sourceKind: SourceKindValue, sourceId: string, entries: readonly ImportRowEntry[]): ImportRowEntry[] {
  const start = rows.findIndex((row) => row.sourceKind === sourceKind && row.sourceId === sourceId && !CHILD_ENTRY_KINDS.includes(row.sourceKind));
  if (start < 0) return [...rows, ...entries];
  let end = start + 1;
  while (end < rows.length && CHILD_ENTRY_KINDS.includes(rows[end]!.sourceKind)) end += 1;
  return [...rows.slice(0, start), ...entries, ...rows.slice(end)];
}

/** A run's four counts, read off its entries, every entry counted as the run's own `rows` count it. */
export function importRowCounts(rows: readonly ImportRowEntry[]): { createdCount: number; updatedCount: number; unchangedCount: number; rejectedCount: number } {
  return {
    createdCount: rows.filter((row) => row.outcome === 'Created').length,
    updatedCount: rows.filter((row) => row.outcome === 'Updated').length,
    unchangedCount: rows.filter((row) => row.outcome === 'Unchanged').length,
    rejectedCount: rows.filter((row) => row.outcome === 'Rejected').length,
  };
}

/** Block 2's three counts, read off a run's rows: merged on the tax number, on name and postal code, and left unmatched. */
/** 🔒 Decision 8381: the CRM manufacturers a read matched on the maker an item here names; nought on every Books read. */
export function itemMakerMatchCount(rows: readonly ImportRowEntry[]): number {
  return rows.filter((row) => row.sourceKind === 'Contact' && row.match === 'itemMaker').length;
}

export function contactMatchCounts(rows: readonly ImportRowEntry[]): { ein: number; emailDomain: number; namePostal: number; unmatched: number } {
  const contacts = rows.filter((row) => row.sourceKind === 'Contact');
  return {
    ein: contacts.filter((row) => row.match === 'ein').length,
    emailDomain: contacts.filter((row) => row.match === 'emailDomain').length,
    namePostal: contacts.filter((row) => row.match === 'namePostal').length,
    unmatched: contacts.filter((row) => row.match === 'none').length,
  };
}

/** The distinct unit strings a block 3 run could not place, blank included, in first-seen order. */
export function unplacedUnits(rows: readonly ImportRowEntry[]): string[] {
  const seen: string[] = [];
  for (const row of rows) if (row.unitText !== undefined && !seen.includes(row.unitText)) seen.push(row.unitText);
  return seen;
}

/**
 * 🔒 The manufacturers a block 3 run creates or would create (decision 1514),
 * once per normalised name in first-seen order: ten items naming Belimo are
 * one Belimo. A rejected row creates nothing, so it names none.
 */
export function manufacturersToCreate(rows: readonly ImportRowEntry[]): string[] {
  const seen = new Map<string, string>();
  for (const row of rows) {
    if (row.outcome === 'Rejected' || !row.createsManufacturer) continue;
    const key = normaliseCompanyName(row.createsManufacturer);
    if (!seen.has(key)) seen.set(key, row.createsManufacturer);
  }
  return [...seen.values()];
}

/** The source system a unit alias is keyed on for this preset. */
export const ZOHO_BOOKS_SOURCE_SYSTEM = 'ZohoBooks';

// ---- The routes the import screen reads, relative to the API root (piece 31) ----

export const IMPORT_PATH = '/import';
export const ZOHO_BOOKS_IMPORT_PATH = `${IMPORT_PATH}/zoho-books`;
export const ZOHO_BOOKS_TEST_PATH = `${ZOHO_BOOKS_IMPORT_PATH}/test`;
export const ZOHO_BOOKS_RUNS_PATH = `${ZOHO_BOOKS_IMPORT_PATH}/runs`;
/** 🔒 Reverse the Opening Entry (decision 3482), behind the importer's administrative capability. */
export const ZOHO_BOOKS_OPENING_REVERSE_PATH = `${ZOHO_BOOKS_IMPORT_PATH}/opening/reverse`;
export const UNIT_ALIASES_PATH = `${IMPORT_PATH}/unit-aliases`;
export const IMPORT_UNITS_PATH = `${IMPORT_PATH}/units`;

export function importRunPath(runId: string): string {
  return `${IMPORT_PATH}/runs/${encodeURIComponent(runId)}`;
}

export function importRunCancelPath(runId: string): string {
  return `${importRunPath(runId)}/cancel`;
}

/** The screen's own address, the route the SYSTEM row opens. */
export const IMPORT_SCREEN_ROUTE = '/admin/import';
export const ZOHO_BOOKS_SETTINGS_ROUTE = '/settings/system/zoho-books';
export const PRICE_LIST_IMPORT_ROUTE = '/settings/catalog/import-price-list';

/** The seeded file type an import run's diff is stored under (piece 31), filed on the Organization's own Company. */
export const IMPORT_REPORT_FILE_TYPE_NAME = 'Import Report';
